import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Inbox, ShieldCheck, ShieldAlert, AlertCircle, FileWarning, UserCog } from "lucide-react";
import { DocsUploader } from "@/pages/PartnerRegister";
import { VENDOR_DOC_KEYS, PROFESSIONAL_DOC_KEYS } from "@/lib/partnerOptions";

const STATUSES = ["new", "contacted", "in_discussion", "won", "lost"];

const inr = (paise) => {
  if (paise == null || paise === "") return "—";
  const n = Number(paise);
  if (!Number.isFinite(n)) return "—";
  return "₹" + (n / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 });
};

const ist = (iso) => new Date(iso).toLocaleString("en-IN", {
  timeZone: "Asia/Kolkata", day: "2-digit", month: "short",
  hour: "2-digit", minute: "2-digit",
});

/**
 * The customer's actual requirement: plot dimensions and the plan they
 * enquired about. Both already arrived in /partner/leads and were previously
 * dropped — for a house-plan marketplace this IS the brief a contractor
 * needs before calling. Renders nothing when a lead carries neither, since
 * leads captured from the contact form often have no plot data.
 */
function LeadRequirement({ lead }) {
  const plot = lead.plot_details && typeof lead.plot_details === "object"
    ? Object.entries(lead.plot_details).filter(([, v]) => v !== null && v !== "")
    : [];
  if (!plot.length && !lead.plan) return null;
  return (
    <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3"
         data-testid={`lead-requirement-${lead.id}`}>
      {plot.length > 0 && (
        <>
          <div className="text-xs uppercase tracking-wider text-slate-500">Plot requirement</div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-800">
            {plot.map(([k, v]) => (
              <span key={k}>
                <span className="text-slate-500">{k.replace(/_/g, " ")}: </span>
                {typeof v === "object" ? JSON.stringify(v) : String(v)}
              </span>
            ))}
          </div>
        </>
      )}
      {lead.plan && (
        <div className={`text-sm text-slate-800 ${plot.length ? "mt-2" : ""}`}>
          <span className="text-slate-500">Plan of interest: </span>{lead.plan.title}
        </div>
      )}
    </div>
  );
}

/**
 * Entries are immutable. There is no edit — to say something new you file
 * another entry, which is what makes the admin timeline trustworthy.
 * Stages move forward only: the current one can be repeated week after week
 * (excavation takes weeks), but never reversed.
 */
function WorkUpdates({ assignment, onFiled }) {
  const [open, setOpen] = useState(false);
  const [stages, setStages] = useState([]);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    stage_id: "", percent_complete: "", amount_paid_rupees: "",
    contract_value_rupees: "", note: "", completed: false,
  });

  const id = assignment.assignment_id;
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Both requests race in parallel; a single join point collapses them to at
  // most one alert. A failed allowed-stages call used to leave stages at [],
  // which reads exactly like "you have no stages available" — the panel
  // closes instead so a failed load doesn't masquerade as an empty one.
  const load = () => {
    Promise.allSettled([
      api.get(`/partner/assignments/${id}/allowed-stages`),
      api.get(`/partner/assignments/${id}/updates`),
    ]).then(([stagesResult, historyResult]) => {
      if (!mountedRef.current) return;
      if (stagesResult.status === "fulfilled") setStages(stagesResult.value.data);
      if (historyResult.status === "fulfilled") setHistory(historyResult.value.data);
      const failed = [stagesResult, historyResult].find(r => r.status === "rejected");
      if (failed) {
        window.alert(formatApiErrorDetail(failed.reason.response?.data?.detail) || failed.reason.message);
        setOpen(false);
      }
    });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) load(); }, [open, id]);

  const file = async () => {
    if (!draft.stage_id) { window.alert("Pick the stage you are working on."); return; }
    setSaving(true);
    try {
      await api.post(`/partner/assignments/${id}/updates`, {
        kind: "stage",
        stage_id: draft.stage_id,
        percent_complete: draft.percent_complete === "" ? null : Number(draft.percent_complete),
        amount_paid_paise: draft.amount_paid_rupees === "" ? null
          : Math.round(Number(draft.amount_paid_rupees) * 100),
        contract_value_paise: draft.contract_value_rupees === "" ? null
          : Math.round(Number(draft.contract_value_rupees) * 100),
        project_status: draft.completed ? "completed" : null,
        note: draft.note || null,
      });
      setDraft({ stage_id: "", percent_complete: "", amount_paid_rupees: "",
                 contract_value_rupees: "", note: "", completed: false });
      load();
      onFiled();
    } catch (err) {
      window.alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (assignment.status !== "won") {
    return (
      <p className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-500">
        Progress updates open once this lead is marked <strong>won</strong>.
      </p>
    );
  }

  return (
    <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`work-updates-${id}`}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        <div><span className="text-xs text-slate-500">Scope</span>{" "}
          <span className="font-medium text-slate-900">{assignment.scope_label || "Full build"}</span></div>
        <div><span className="text-xs text-slate-500">Stage</span>{" "}
          <span className="font-medium text-slate-900">{assignment.current_stage_name || "—"}</span></div>
        <div><span className="text-xs text-slate-500">Complete</span>{" "}
          <span className="font-medium text-slate-900">
            {assignment.percent_complete != null ? `${assignment.percent_complete}%` : "—"}</span></div>
        <div><span className="text-xs text-slate-500">Received</span>{" "}
          <span className="font-medium text-slate-900">{inr(assignment.amount_paid_paise)}</span></div>
        <div className="text-xs text-slate-500">
          {assignment.last_update_at ? `Last update ${ist(assignment.last_update_at)}` : "No updates yet"}
        </div>
      </div>

      <button onClick={() => setOpen(o => !o)}
              className="mt-3 text-sm text-emerald-700 font-medium hover:text-emerald-800"
              data-testid={`add-update-toggle-${id}`}>
        {open ? "Close" : "Add progress update"}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <label className="flex flex-col md:col-span-2">
              <span className="text-xs text-slate-500">Stage you are working on</span>
              <select className="nc-input py-2" value={draft.stage_id}
                      onChange={e => setDraft(d => ({ ...d, stage_id: e.target.value }))}
                      data-testid={`update-stage-${id}`}>
                <option value="">Select…</option>
                {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <span className="text-xs text-slate-400 mt-1">
                Only stages in your scope appear, from your current stage onwards.
                Still on the same stage? Select it again.
              </span>
            </label>
            <label className="flex flex-col">
              <span className="text-xs text-slate-500">% of your work complete</span>
              <input className="nc-input py-2" type="number" min="0" max="100"
                     value={draft.percent_complete}
                     onChange={e => setDraft(d => ({ ...d, percent_complete: e.target.value }))}
                     data-testid={`update-percent-${id}`} />
            </label>
            <label className="flex flex-col">
              <span className="text-xs text-slate-500">Total received so far (₹)</span>
              <input className="nc-input py-2" type="number" min="0"
                     value={draft.amount_paid_rupees}
                     onChange={e => setDraft(d => ({ ...d, amount_paid_rupees: e.target.value }))}
                     data-testid={`update-paid-${id}`} />
              <span className="text-xs text-slate-400 mt-1">
                Running total, not this week's payment.
              </span>
            </label>
            <label className="flex flex-col md:col-span-2">
              <span className="text-xs text-slate-500">Revised contract value (₹) — only if it changed</span>
              <input className="nc-input py-2" type="number" min="0"
                     value={draft.contract_value_rupees}
                     onChange={e => setDraft(d => ({ ...d, contract_value_rupees: e.target.value }))}
                     data-testid={`update-value-${id}`} />
            </label>
            <label className="flex flex-col md:col-span-2">
              <span className="text-xs text-slate-500">Note</span>
              <textarea className="nc-input py-2" rows={2} value={draft.note}
                        onChange={e => setDraft(d => ({ ...d, note: e.target.value }))}
                        data-testid={`update-note-${id}`} />
            </label>
            <label className="flex items-center gap-2 md:col-span-2 text-slate-700">
              <input type="checkbox" checked={draft.completed}
                     onChange={e => setDraft(d => ({ ...d, completed: e.target.checked }))}
                     data-testid={`update-completed-${id}`} />
              Work is complete — stop the weekly reminders
            </label>
          </div>
          <button onClick={file} disabled={saving} className="nc-btn-primary py-2"
                  data-testid={`update-submit-${id}`}>
            {saving ? "Saving…" : "File update"}
          </button>
          <p className="text-xs text-slate-400">
            Entries can't be edited afterwards. If something is wrong, file another
            entry or ask NakshaCart to void it.
          </p>

          {history.length > 0 && (
            <div className="pt-3 border-t border-slate-100">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Your past updates</div>
              <div className="space-y-2">
                {history.map(h => (
                  <div key={h.id} className={`text-sm ${h.voided_at ? "text-slate-400 line-through" : "text-slate-700"}`}>
                    <span className="text-xs text-slate-500">{ist(h.created_at)}</span>{" "}
                    {h.kind === "stage" ? (h.stage_name || "Stage") : h.pipeline_status}
                    {h.percent_complete != null ? ` · ${h.percent_complete}%` : ""}
                    {h.amount_paid_paise != null ? ` · ${inr(h.amount_paid_paise)}` : ""}
                    {h.note ? ` · "${h.note}"` : ""}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PartnerDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [leads, setLeads] = useState([]);

  const refresh = () => {
    api.get("/partner/me").then(({ data }) => setProfile(data)).catch(() => {});
    api.get("/partner/leads").then(({ data }) => setLeads(data));
  };
  useEffect(() => { refresh(); }, []);

  const updateStatus = async (assignment_id, status, notes = "") => {
    await api.post("/partner/leads/status", { assignment_id, status, notes });
    refresh();
  };

  // Company name lives in details.company_name for vendors; professionals don't have one,
  // so fall back to nothing rather than printing "(undefined)".
  const companyName = profile?.details?.company_name || "";

  const StatusBadge = profile?.status === "approved"
    ? <span className="nc-chip"><ShieldCheck className="w-3 h-3" /> Approved</span>
    : profile?.status === "pending"
      ? <span className="nc-chip nc-chip-blue"><ShieldAlert className="w-3 h-3" /> Pending Approval</span>
      : <span className="nc-chip" style={{ background: "#FEE2E2", color: "#B91C1C" }}><AlertCircle className="w-3 h-3" /> {profile?.status}</span>;

  // Document checklist based on partner_type. We treat any key already present in
  // profile.documents as uploaded; user can still re-upload to overwrite.
  const docKeys = profile?.partner_type === "vendor" ? VENDOR_DOC_KEYS : PROFESSIONAL_DOC_KEYS;
  const uploadedMap = Object.fromEntries(
    Object.entries(profile?.documents || {}).map(([k, v]) => [k, v?.filename || k])
  );
  const missingCount = docKeys.filter(d => !uploadedMap[d.key]).length;

  return (
    <div className="bg-slate-50/40 min-h-screen py-8" data-testid="partner-dashboard">
      <div className="nc-container">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>
              Hello, {user?.name}
              {companyName && <span className="text-slate-500 font-normal"> ({companyName})</span>}
            </h1>
            <p className="text-slate-500 mt-1">{profile ? `${profile.state} • ${profile.district} • ${profile.professions?.join(", ")}` : "Partner"}</p>
            {profile && (
              <a
                href="#partner-docs-section"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("partner-docs-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-800"
                data-testid="partner-update-profile-btn"
              >
                <UserCog className="w-4 h-4" /> Update Profile
              </a>
            )}
          </div>
          {profile && StatusBadge}
        </div>

        {profile?.status === "pending" && (
          <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800" data-testid="pending-banner">
            Your account is pending admin approval. You'll start receiving Project/Work as soon as you're approved (typically within 24 hours).
          </div>
        )}

        {profile && missingCount > 0 && (
          <div className="mt-4 bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3" data-testid="missing-docs-banner">
            <FileWarning className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-rose-800">
              <strong>{missingCount} document{missingCount > 1 ? "s" : ""} missing.</strong> Upload them below — admins typically approve faster when all KYC documents are present.
            </div>
          </div>
        )}

        {profile && (
          <section id="partner-docs-section" className="mt-6 bg-white rounded-2xl border border-slate-200 p-5" data-testid="partner-docs-section">
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Your documents</h2>
            <p className="text-sm text-slate-500 mt-1">Resume uploading any document you skipped during registration. PDF / JPG / PNG, up to 10 MB each.</p>
            <div className="mt-4">
              <DocsUploader
                docs={docKeys}
                partnerId={profile.id}
                testidPrefix="dash-doc"
                initialUploaded={uploadedMap}
              />
            </div>
          </section>
        )}

        <h2 className="mt-8 text-xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Assigned Leads</h2>
        {leads.length === 0 ? (
          <div className="mt-4 bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-500">
            <Inbox className="w-8 h-8 mx-auto text-slate-300" />
            <p className="mt-2">No leads assigned yet.</p>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {leads.map(a => (
              <div key={a.assignment_id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`lead-${a.assignment_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-slate-900">{a.lead.name}</div>
                    <div className="text-sm text-slate-500">{a.lead.mobile} • {a.lead.email || "No email"} • {a.lead.state} {a.lead.district}</div>
                    <div className="text-sm text-slate-700 mt-2">{a.lead.message}</div>
                    <LeadRequirement lead={a.lead} />
                  </div>
                  <select
                    className="nc-input max-w-[180px] py-2"
                    value={a.status}
                    onChange={(e) => updateStatus(a.assignment_id, e.target.value)}
                    data-testid={`lead-status-${a.assignment_id}`}
                  >
                    {STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <WorkUpdates assignment={a} onFiled={refresh} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
